import { Button } from 'components/button';
import { Icon, IconsEnum } from 'components/Icons';
import { usePrenotaScommessaInitSessionMutation, usePrenotaScommessaMutation } from 'features/api/sportApiSlice';
import { tabSelected } from 'features/carrello/carrelloSlice';
import { selectTicket } from 'features/sport/components/sportTicket/sportTicketSelectors';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { SportPrenotaScommessaInitSessionResponse } from 'lib/api/sport/sportPrenotaScommessaInitSessionApi';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import Persist, { StorageKind } from 'lib/persist';
import { deleteCookie, getCookie, setCookie } from 'lib/persist/cookie';
import { useRouter } from 'next/router';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { isClientSide } from 'utility/constant';
import { getRouteByKey, PathName } from 'utility/routes';
import { toggleCarrello } from '../actions';
import { CarrelloDialog } from '../carrelloDialog';
import { selectTipo } from '../carrelloSelectors';
import { isLoadingVenditaChanged } from '../carrelloSlice';
import { PRENOTAZIONE_SCOMMESSA_CODICESESSIONE } from '../constant';
import { CarrelloTabEnum } from '../types';
import styles from './CarrelloActionPrenotazione.module.scss';

const localStorage = Persist(StorageKind.local);

export type CarrelloActionPrenotazioneProps = {
  btnLabel: string;
  btnClassName?: string;
  disabled?: boolean;
};

export const CarrelloActionPrenotazione = memo(function CarrelloActionPrenotazione({
  btnLabel,
  btnClassName,
  disabled,
}: CarrelloActionPrenotazioneProps) {
  const tipo = useTypedSelector(selectTipo);
  const ticket = useTypedSelector(selectTicket);
  const [isPrenotata, setIsPrenotata] = useState<boolean>(false);
  const [codicePrenotazione, setCodicePrenotazione] = useState<string>('');
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { asPath, push } = router;
  const registrationUrl = useMemo(() => getRouteByKey(PathName.registrationUrl), []);

  const slugProvenienza = useMemo(() => {
    const splittedSlug = asPath.split('/');
    let slug = '';
    if (splittedSlug.length > 1) {
      slug = splittedSlug[1]?.split('?')?.[0];
    } else {
      slug = splittedSlug[0];
    }
    return slug || 'homepage';
  }, [asPath]);

  const [prenotazioneScommessaInitSession, setPrenotazioneScommessaInitSession] =
    useState<SportPrenotaScommessaInitSessionResponse>();

  const [prenotaScommessaInitSession, { isLoading: isLoadingInitSession }] = usePrenotaScommessaInitSessionMutation();
  const [prenotaScommessa, { isLoading: isLoadingPrenotaScommessa }] = usePrenotaScommessaMutation();

  const [timeToDelay, setTimeToDelay] = useState<number[]>([]);

  const labelScommessaPrenotata = useGetLabelByKey('scommessa-prenotata');
  const labelVediPrenotazioni = useGetLabelByKey('vedi-prenotazioni');
  const labelComunicazioneNickname = useGetLabelByKey('vai-e-comunica-il-tuo-nickname');
  const labelValiditaPrenotazione = useGetLabelByKey('tempo-validita-prenotazione');
  const labelRegistrati = useGetLabelByKey('register');
  const labelInvitoAllaRegistrazione = useGetLabelByKey('invito-alla-registrazione');
  const labelWithTime = useMemo(
    () =>
      timeToDelay
        ? labelValiditaPrenotazione!
            .replace('{{valoreOre}}', `${timeToDelay.length > 0 ? timeToDelay[0] : 23}`)
            .replace('{{valoreMinuti}}', `${timeToDelay.length > 0 ? timeToDelay[1] : 59}`)
        : '',
    [labelValiditaPrenotazione, timeToDelay]
  );

  useEffect(() => {
    let prenotazioneCookie = getCookie(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`);

    if (prenotazioneCookie) {
      const cookieValueParts = prenotazioneCookie && prenotazioneCookie.split('_');
      const scadenzaCodiceSessione = cookieValueParts[1];

      const currentDate = new Date();
      const expireDate = new Date(scadenzaCodiceSessione);
      const timeDifference = expireDate.getTime() - currentDate.getTime();
      const hoursDifference = Math.floor(timeDifference / (1000 * 60 * 60));
      const minutesDifference = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
      setTimeToDelay([hoursDifference, minutesDifference]);

      if (currentDate > expireDate) {
        deleteCookie(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`);
        localStorage.removeItem(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`);
      }
    } else {
      const existingLsElement = localStorage.getItem(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`);
      if (existingLsElement) {
        localStorage.removeItem(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`);
      }
    }
  }, [tipo]);

  const handlePrenotaScommessaInitSession = useCallback(async () => {
    try {
      const response = await prenotaScommessaInitSession().unwrap();
      setPrenotazioneScommessaInitSession(response);
      const cookieThreshold = 24 * 60 * 60; // 1 day (in seconds)
      setCookie(
        `${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`,
        `${response.codicesessione}_${response.scadenza}`,
        cookieThreshold
      );
    } catch (error) {
      console.error('rejected', error);
    }

    return getCookie(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`);
  }, [prenotaScommessaInitSession, tipo]);

  useEffect(() => {
    if (isLoadingInitSession || isLoadingPrenotaScommessa) {
      dispatch(isLoadingVenditaChanged(true));
    } else {
      dispatch(isLoadingVenditaChanged(false));
    }
  }, [dispatch, isLoadingInitSession, isLoadingPrenotaScommessa]);

  const handleClick = useCallback(
    (tab: CarrelloTabEnum) => {
      dispatch(toggleCarrello({ isFullCart: true, isOpen: false }));
      setTimeout(() => {
        dispatch(toggleCarrello({ isOpen: true }));
      }, 200);
      dispatch(tabSelected(tab));
    },
    [dispatch]
  );

  const handlePrenotaScommessa = useCallback(
    async (prenotazioneCookie: string | null) => {
      if (prenotazioneCookie && ticket) {
        try {
          const response = await prenotaScommessa({
            codiceSessione: prenotazioneCookie,
            ...ticket,
          }).unwrap();

          //Retrieve the array from localStorage
          const existingLsElement = localStorage.getItem(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`);
          //If there's nothing in localStorage, create an empty array
          const existingArray = existingLsElement ? JSON.parse(existingLsElement) : [];
          if (response.id_prenotazione) {
            //Add a new element to the array
            const currPrenotazione = {
              idPrenotazione: response.id_prenotazione,
              ...ticket,
            };
            existingArray.push(JSON.stringify(currPrenotazione));
            //Update the modified array back in localStorage
            localStorage.setItem(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`, JSON.stringify(existingArray));
            if (isClientSide) {
              window.dispatchEvent(new Event('storage'));
            }

            if (response.stato === 1024) {
              setIsPrenotata(true);
            }
          }
        } catch (error) {
          console.error('rejected', error);
        }
      }
    },
    [ticket, tipo, prenotaScommessa]
  );

  const handleClickPrenota = useCallback(async () => {
    try {
      let prenotazioneCookie = getCookie(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`);

      if (prenotazioneCookie === null || prenotazioneCookie === '') {
        prenotazioneCookie = await handlePrenotaScommessaInitSession();
      }

      if (prenotazioneCookie) {
        const cookieValueParts = prenotazioneCookie && prenotazioneCookie.split('_');
        const codiceSessione = cookieValueParts[0];
        const scadenzaCodiceSessione = cookieValueParts[1];

        const currentDate = new Date();
        const expireDate = new Date(scadenzaCodiceSessione);

        // Compare the two Date objects
        if (currentDate < expireDate) {
          setCodicePrenotazione(codiceSessione);
          handlePrenotaScommessa(codiceSessione);
        } else {
          //delete cookie
          deleteCookie(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`);
          //delete localStorage
          localStorage.removeItem(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`);
          //init new session
          const newPrenotazioneCookie = await handlePrenotaScommessaInitSession();
          if (newPrenotazioneCookie) {
            const cookieValueParts = newPrenotazioneCookie && newPrenotazioneCookie.split('_');
            setCodicePrenotazione(cookieValueParts[0]);
            handlePrenotaScommessa(cookieValueParts[0]);
          }
        }
      }
    } catch (err) {
      console.error('Failed prenotazione: ', err);
    }
  }, [handlePrenotaScommessaInitSession, handlePrenotaScommessa, tipo]);

  return (
    <React.Fragment>
      <Button
        type="button"
        className={`${styles.button} ${btnClassName && btnClassName}`}
        size="medium"
        visualizationType={'tertiary'}
        onClick={() => handleClickPrenota()}
        isLoading={isLoadingInitSession || isLoadingPrenotaScommessa}
        disabled={disabled}
      >
        {btnLabel}
      </Button>
      <CarrelloDialog
        type="success"
        title={labelScommessaPrenotata!}
        text={``}
        open={isPrenotata}
        defaultOpen={isPrenotata}
        onOpenChange={setIsPrenotata}
        modal={false}
      >
        <p className={styles.text}>
          {labelComunicazioneNickname}
          <br />
          {labelWithTime}
        </p>
        <div className={styles.nickname}>{prenotazioneScommessaInitSession?.nickname || codicePrenotazione}</div>
        <Button
          size={'medium'}
          visualizationType={'secondary'}
          className={`${styles.button}`}
          onClick={() => handleClick(CarrelloTabEnum.Scommesse)}
        >
          {labelVediPrenotazioni}
        </Button>
        <div className={styles.registrazioneContainer}>
          <div className={styles.apriUnContoContainer}>
            <Icon iconId={IconsEnum.SNAI_LOGO_SHORT} color="" className={styles.logoSnai} />
            <p> {labelInvitoAllaRegistrazione}</p>
          </div>
          <Button
            size={'medium'}
            visualizationType={'primary'}
            className={`${styles.button} ${styles.registratiBtn} `}
            onClick={() =>
              push(
                {
                  ...router,
                  pathname: registrationUrl,
                  query: {
                    ...router.query,
                    provenienza: `newsnai_${slugProvenienza}`,
                  },
                },
                undefined,
                { shallow: true }
              )
            }
          >
            {labelRegistrati}
          </Button>
        </div>

        {/* <div className={styles.codicePrenotazioneTextContainer}>
          <span className={styles.codicePrenotazioneText}>
            Id Prenotazione -
          </span>
          <span className={styles.codicePrenotazione}>
            {prenotazioneScommessa?.idtrans}
          </span>
          <p>
            Ricorda di salvare il codice per comunicarlo correttamente nel Punto
            SNAI
          </p>
        </div> */}
      </CarrelloDialog>
    </React.Fragment>
  );
});
